import React, {useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import {AddOutlined, DeleteOutlined} from '@material-ui/icons';
import PageWrapper from '../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../shared/pagetitle/PageTitle.js';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import BlockUI from '../../../shared/blockui/BlockUI.tsx';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import DialogSelecionarPessoaAtendente from '../../dialogs/cadastro/DialogSelecionarPessoaAtendente.jsx';
import FotoPessoa from '../../components/cadastro/FotoPessoa.jsx';
import {
    CONFIRMACAO_EXCLUIR_ATENDENTE,
    MENSAGEM_ATENDENTE_ADICIONADO,
    MENSAGEM_ATENDENTE_EXCLUIDO,
    montarPayloadAtendente
} from '../../components/cadastro/cadastroAtendimento.js';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import PageNaoEncontrada from '../../../PageNaoEncontrada.jsx';
import useCheckModule from '../../../../hooks/useCheckModule.js';
import useDialogState from '../../../../hooks/useDialogState.js';
import useFetch from '../../../../hooks/useFetch.js';
import useCreate from '../../../../hooks/useCreate.js';
import useRemove from '../../../../hooks/useRemove.js';
import {URL_ATENDENTES} from '../../../../constants/index.js';
import {buttonTableCell, containerPaper} from '../../../../utils/styles.js';
import {PAGINACAO_PADRAO, textoSemRegistros} from '../../components/paginacao.js';

const PARAMS_VAZIOS = {};
const FILTER_OPTIONS = {filterByColumn: true};
const TABLE_CONTAINER_PROPS = {component: 'div'};

const useStyles = makeStyles(theme => ({
    ...containerPaper(theme),
    colunaFoto: {
        width: theme.spacing(9)
    },
    ...buttonTableCell(theme)
}));

/*
 * Atendentes (módulo CFATENDENT): lista de pessoas que atendem. "Adicionar atendente" abre a
 * busca de pessoas, que fica aberta: cada "Adicionar" grava, recarrega a lista da página e a
 * do diálogo. Os erros do back (duplicado, em uso) já são mostrados pelos hooks.
 */
export default function PageAtendentes() {
    const classes = useStyles();
    const checkModule = useCheckModule();
    const podeAcessar = checkModule('CFATENDENT');
    const [fetch, carregando] = useFetch({returnInnerLoadingState: true});
    const [create, criando] = useCreate({returnInnerLoadingState: true});
    const [remove, removendo] = useRemove({returnInnerLoadingState: true});
    const confirmation = useConfirmationDialog();
    const {createToast} = useToast();
    const {dialogState, openDialog, closeDialog} = useDialogState({pessoas: false});
    const [itens, setItens] = useState([]);
    // Incrementada a cada gravação: o diálogo refaz a busca e a pessoa adicionada some.
    const [atualizacaoPessoas, setAtualizacaoPessoas] = useState(0);

    // A credencial renova a cada resposta: os callbacks chamam sempre o fetch do render mais recente.
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const carregar = () => fetchRef.current(URL_ATENDENTES, PARAMS_VAZIOS, dados => setItens(Array.isArray(dados) ? dados : []));

    useEffect(() => {
        if (podeAcessar) {
            carregar();
        }
    }, [podeAcessar]);

    const adicionar = pessoa => create(URL_ATENDENTES, montarPayloadAtendente(pessoa.idPessoa), () => {
        createToast({severity: 'success', message: MENSAGEM_ATENDENTE_ADICIONADO});
        carregar();
        setAtualizacaoPessoas(valor => valor + 1);
    });

    const excluir = atendente => {
        confirmation(CONFIRMACAO_EXCLUIR_ATENDENTE, () =>
            remove(`${URL_ATENDENTES}/${atendente.id}`, () => {
                createToast({severity: 'success', message: MENSAGEM_ATENDENTE_EXCLUIDO});
                carregar();
            }));
    };

    if (!podeAcessar) {
        return <PageNaoEncontrada/>;
    }

    return (
        <PageWrapper>
            <PageTitle title='Atendentes'/>
            <Paper className={classes.containerPaper}>
                <BlockUI loading={carregando || criando || removendo}>
                    <DataGrid
                        rows={itens}
                        comparatorKeys={['id']}
                        filterOptions={FILTER_OPTIONS}
                        paginate
                        paginateOptions={PAGINACAO_PADRAO}
                        tableContainerProps={TABLE_CONTAINER_PROPS}
                        noRecordsText={textoSemRegistros(carregando)}
                        toolbarContent={(
                            <ContainedButton startIcon={<AddOutlined/>} onClick={openDialog('pessoas')}>
                                Adicionar atendente
                            </ContainedButton>
                        )}
                        columns={[
                            {
                                label: 'Foto',
                                filterable: false,
                                className: classes.colunaFoto,
                                valueGetter: (valor, row) => <FotoPessoa urlFoto={row.urlFoto} nome={row.nome}/>
                            },
                            {label: 'Nome do atendente', field: 'nome', filterable: true, valueGetter: valor => ouTraco(valor)},
                            criarColunaOpcoes(row => [
                                {titulo: 'Excluir', icone: <DeleteOutlined/>, onClick: () => excluir(row)}
                            ], {className: classes.buttonTableCell})
                        ]}/>
                </BlockUI>
            </Paper>
            <DialogSelecionarPessoaAtendente
                open={dialogState.pessoas}
                onClose={closeDialog('pessoas')}
                onSelecionar={adicionar}
                atualizacao={atualizacaoPessoas}
                ocupado={criando}/>
        </PageWrapper>
    );
}
